import Avatar from "../../../../parts/avatar";
import { formatNumber, formatStripeDate } from "../../../../utils/format-sub";
import { getDivisor } from "../../../../utils/stripe-helpers";
import ToolTip from "../../../../parts/tooltip-portal";
import SvgIcon from "../../../../parts/SvgIcon";
import { truncateText } from "../../../../utils/string-helpers";
import submit from "submit-form";

const DetailsComponent = ({
  sub,
  toggleDrawer,
  type = "membership",
  handleCancel = () => {},
  subscriberInvoices = {},
  setStep,
}) => {
  const { invoices, grossRevenue, netRevenue, referral } = subscriberInvoices;

  const divisor = getDivisor(sub?.currency);
  const hasFacilikey = !!sub?.plan?.metadata?.facilikey;

  return (
    <>
      <header className="details-header">
        <hgroup>
          {sub?.customer_avatar ? (
            <Avatar
              size="96"
              url={sub.customer_avatar.url}
              user={{}}
              height="auto"
            />
          ) : (
            <div className="subscriber-icon"></div>
          )}
          <div>
            <h2>{truncateText(sub?.customer_name, 30)}</h2>
            <p className="p1">{truncateText(sub?.customer_email, 40)}</p>
            {!!sub?.customer_token && (
              <div className="chip info">
                Onboarding code: {sub?.customer_token}
              </div>
            )}
          </div>
        </hgroup>
        <a
          href="#_"
          onClick={toggleDrawer}
          role="button"
          data-button-type="tertiary"
          data-icon-only="true"
        >
          <img src="/img/new-icons/red-cross.svg" alt="dismiss" />
        </a>
      </header>

      <section>
        {type === "membership" && (
          <p className="p2">
            Phone number: <span>{sub?.phoneNumber}</span>
          </p>
        )}
        <p className="p2">
          Joining date: <span>{sub?.created}</span>
        </p>
        <p className="p2">
          Gross revenue:{" "}
          <span>{formatNumber(grossRevenue / divisor, sub?.currency)}</span>
        </p>
        <p className="p2">
          <div>
            Net revenue:{" "}
            <ToolTip
              body={[
                "This value doesn't take into account Stripe fees. Stripe fees are 2.9% + $0.30 USD for each charge",
              ]}
            >
              <div className="info">
                <img src="/img/svg/question-circle.svg" />
              </div>
            </ToolTip>
          </div>
          <span>{formatNumber(netRevenue / divisor, sub?.currency)}</span>
        </p>

        <p className="p2">
          Plan:{" "}
          <span>
            <span>{truncateText(sub?.plan.interval, 30)}</span>{" "}
            {sub?.canceled ? (
              <span className="!text-[--danger]">(Cancelled)</span>
            ) : null}
          </span>
        </p>
        {hasFacilikey && (
          <p className="p2">
            <div>
              Facilikey{" "}
              <button
                onClick={() => {
                  submit("/actions/user/facilikey-resync", {
                    method: "POST",
                    body: {
                      customer: sub.customer,
                    },
                  });
                }}
                className="p-0 text-[--information] !text-md underline"
              >
                (Re-sync key)
              </button>
            </div>
            <span>Yes</span>
          </p>
        )}
        <p className="p2">
          Last payment: <span>{sub?.current_period_start}</span>
        </p>
        {!!referral && (
          <p className="p2">
            Referred by:{" "}
            <span>
              {referral?.referrerId?.name || "-"} (
              {referral?.referrerId?.email || "-"})
            </span>
          </p>
        )}
        <div className="member-details-actions">
          {!sub?.canceled && type === "membership" && (
            <a
              role="button"
              data-button-type="outline"
              href="#_"
              className="secondary-button"
              onClick={(e) => {
                e.preventDefault();
                setStep("change");
              }}
            >
              <span>Change plan</span>
            </a>
          )}

          {!sub?.canceled && (
            <a
              role="button"
              data-button-type="secondary"
              data-action="cancel"
              href="#_"
              className="cancel"
              onClick={handleCancel}
            >
              Cancel {type}
            </a>
          )}
        </div>
      </section>

      <section>
        {type === "membership" && (
          <p className="p2 justify-start gap-2">
            Contract signed:{" "}
            <img
              src="/img/new-icons/check-gradient.svg"
              alt="check"
              height={14}
              width={14}
            />
          </p>
        )}
        <details>
          <summary>
            <SvgIcon src={"/img/new-icons/arrow-right.svg"} />
            PAR-Q
          </summary>
          {sub?.parqForm ? (
            <>
              <div>
                <div>
                  <p className="p2">
                    Has a heart condition and should only do physical activity
                    recommended by a doctor:
                  </p>
                  <span>{sub.parqForm.heartCondition ? " Yes" : " No"}</span>
                </div>
                <div>
                  <p className="p2">
                    Feels pain in chest when doing physical activity:
                  </p>
                  <span>{sub.parqForm.chestPain ? " Yes" : " No"}</span>
                </div>
                <div>
                  <p className="p2">
                    Has had chest pain when not doing physical activity in past
                    month:
                  </p>
                  <span>
                    {sub.parqForm.pastMonthChestPain ? " Yes" : " No"}
                  </span>
                </div>
                <div>
                  <p className="p2">
                    Loses balance because of dizziness or has ever lost
                    consciousness:
                  </p>
                  <span>
                    {sub.parqForm.dizzinesConsciousnessLoss ? " Yes" : " No"}
                  </span>
                </div>
                <div>
                  <p className="p2">
                    Has bone or joint problem that could be made worse by a
                    change in physical activity:
                  </p>
                  <span>{sub.parqForm.boneJointProblem ? " Yes" : " No"}</span>
                </div>
                <div>
                  <p className="p2">
                    Doctor currently prescribes drugs for blood pressure or
                    heart condition:
                  </p>
                  <span>
                    {sub.parqForm.bloodPressurePrescription ? " Yes" : " No"}
                  </span>
                </div>
                <div>
                  <p className="p2 ">
                    Has any other reason to not do physical activity:
                  </p>
                  <span>
                    {sub.parqForm.reasonForNoPhysicalAct ? " Yes" : " No"}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <p className="p2 text-center">
              PAR-Q questionnaire not completed yet
            </p>
          )}
        </details>
      </section>

      <section>
        <h3>Payments history</h3>

        <section role="table-wrapper" data-borders-inside>
          {invoices?.length ? (
            <table role="report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody className="table-body-side-subscriber-list">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{formatStripeDate(invoice.created)}</td>
                    <td>
                      {formatNumber(
                        invoice.amount_received / divisor,
                        invoice.currency
                      )}
                    </td>
                    <td>{invoice.status}</td>
                    <td>
                      {invoice.invoice ? (
                        <a
                          href={invoice.invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="clipped"
                        >
                          Invoice
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center">No payments found</p>
          )}
        </section>
      </section>
    </>
  );
};

export default DetailsComponent;
